import React, { useState, useEffect, useCallback } from 'react';
import { useConfirmDialog } from './components/ui/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper per formattare date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '-';
  try {
    const date = new Date(dateTimeStr);
    return date.toLocaleString('it-IT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateTimeStr;
  }
};

// ============ COMPONENTE GESTIONE ISCRIZIONI (per Admin) ============

export function GestioneIscrizioniEvento({ evento, token, onUpdate, onClose }) {
  const { showConfirm } = useConfirmDialog();
  const [iscrizioni, setIscrizioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [statoIscrizioni, setStatoIscrizioni] = useState('');
  const [postiDisponibili, setPostiDisponibili] = useState(null);
  
  // Helper per convertire ISO a datetime-local (mostra ora locale)
  const isoToDatetimeLocal = (isoString) => {
    if (!isoString) return '';
    try {
      // Se è già in formato datetime-local (YYYY-MM-DDTHH:mm), restituisci così com'è
      if (isoString.length === 16 && isoString.includes('T') && !isoString.includes('Z')) {
        return isoString;
      }
      // Converti da ISO UTC a ora locale
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      // Formatta in YYYY-MM-DDTHH:mm locale
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };
  
  // Helper per convertire datetime-local a ISO (salva in UTC)
  const datetimeLocalToIso = (localDatetime) => {
    if (!localDatetime) return null;
    try {
      // Il valore datetime-local è già in ora locale del browser
      const date = new Date(localDatetime);
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    } catch {
      return null;
    }
  };
  
  // Form impostazioni
  const [settings, setSettings] = useState({
    iscrizioni_abilitate: evento.iscrizioni_abilitate || false,
    iscrizioni_apertura: isoToDatetimeLocal(evento.iscrizioni_apertura),
    iscrizioni_chiusura: isoToDatetimeLocal(evento.iscrizioni_chiusura),
    max_partecipanti: evento.max_partecipanti || '',
    regolamento: evento.regolamento || '',
    copertina_url: evento.copertina_url || '',
    tema_torneo: evento.tema_torneo || '',
    iscrizioni_chiuse_manualmente: evento.iscrizioni_chiuse_manualmente || false
  });
  
  // Lista d'attesa separata
  const [listaAttesa, setListaAttesa] = useState([]);
  
  // Form nuovo iscritto/ospite
  const [showAggiungiOspite, setShowAggiungiOspite] = useState(false);
  const [ospiteForm, setOspiteForm] = useState({
    nome: '',
    squadra: '',
    note: ''
  });
  
  // Soci per select
  const [soci, setSoci] = useState([]);
  
  // Upload copertina
  const [uploadingCopertina, setUploadingCopertina] = useState(false);

  const showMsgLocal = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  };

  const api = useCallback(async (endpoint, options = {}) => {
    try {
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });
      return res;
    } catch (err) {
      console.error('API Error:', err);
      return null;
    }
  }, [token]);

  const loadIscrizioni = useCallback(async () => {
    setLoading(true);
    const r = await api(`/api/clubs/calendario/eventi/${evento.id}/iscrizioni`);
    if (r?.ok) {
      const data = await r.json();
      setIscrizioni(data.iscrizioni || []);
      setListaAttesa(data.lista_attesa || []);
      setStatoIscrizioni(data.stato_iscrizioni || '');
      setPostiDisponibili(data.posti_disponibili);
      // Update settings with server data
      if (data.iscrizioni_chiuse_manualmente !== undefined) {
        setSettings(prev => ({...prev, iscrizioni_chiuse_manualmente: data.iscrizioni_chiuse_manualmente}));
      }
    }
    setLoading(false);
  }, [api, evento.id]);

  const loadSoci = useCallback(async () => {
    const r = await api('/api/clubs/anagrafica');
    if (r?.ok) {
      const data = await r.json();
      setSoci(data.soci || []);
    }
  }, [api]);

  useEffect(() => {
    loadIscrizioni();
    loadSoci();
  }, [loadIscrizioni, loadSoci]);

  // Upload copertina dal PC
  const handleCopertinaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Verifica tipo file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showMsgLocal('error', 'Tipo file non supportato. Usa JPG, PNG, GIF o WebP');
      return;
    }
    
    // Verifica dimensione (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      showMsgLocal('error', 'File troppo grande (max 5MB)');
      return;
    }
    
    setUploadingCopertina(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${BACKEND_URL}/api/clubs/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        // Costruisci URL completo
        const fullUrl = `${BACKEND_URL}${data.file.url}`;
        setSettings(prev => ({ ...prev, copertina_url: fullUrl }));
        showMsgLocal('success', 'Immagine caricata!');
      } else {
        const err = await res.json();
        showMsgLocal('error', err.detail || 'Errore upload');
      }
    } catch (err) {
      console.error('Upload error:', err);
      showMsgLocal('error', 'Errore durante il caricamento');
    }
    setUploadingCopertina(false);
    // Reset input file
    e.target.value = '';
  };

  const saveSettings = async () => {
    setSaving(true);
    
    // Converti datetime-local in ISO per il backend usando il helper
    const dataToSend = {
      ...settings,
      max_partecipanti: settings.max_partecipanti ? parseInt(settings.max_partecipanti) : null,
      iscrizioni_apertura: datetimeLocalToIso(settings.iscrizioni_apertura),
      iscrizioni_chiusura: datetimeLocalToIso(settings.iscrizioni_chiusura)
    };
    
    const r = await api(`/api/clubs/calendario/eventi/${evento.id}/iscrizioni-settings`, {
      method: 'PUT',
      body: JSON.stringify(dataToSend)
    });
    
    if (r?.ok) {
      showMsgLocal('success', 'Impostazioni salvate');
      if (onUpdate) onUpdate();
      loadIscrizioni();
    } else {
      showMsgLocal('error', 'Errore salvataggio');
    }
    setSaving(false);
  };

  // Toggle chiusura manuale iscrizioni
  const toggleChiusuraIscrizioni = async () => {
    setSaving(true);
    const nuovoStato = !settings.iscrizioni_chiuse_manualmente;
    
    const r = await api(`/api/clubs/calendario/eventi/${evento.id}/chiudi-iscrizioni`, {
      method: 'POST',
      body: JSON.stringify({ chiudi: nuovoStato })
    });
    
    if (r?.ok) {
      setSettings(prev => ({...prev, iscrizioni_chiuse_manualmente: nuovoStato}));
      showMsgLocal('success', nuovoStato ? 'Iscrizioni chiuse' : 'Iscrizioni riaperte');
      if (onUpdate) onUpdate();
      loadIscrizioni();
    } else {
      showMsgLocal('error', 'Errore aggiornamento');
    }
    setSaving(false);
  };

  const aggiungiOspite = async () => {
    if (!ospiteForm.nome || !ospiteForm.squadra) {
      showMsgLocal('error', 'Nome e squadra obbligatori');
      return;
    }
    
    setSaving(true);
    const r = await api(`/api/clubs/calendario/eventi/${evento.id}/iscrizioni/ospite`, {
      method: 'POST',
      body: JSON.stringify(ospiteForm)
    });
    
    if (r?.ok) {
      showMsgLocal('success', 'Ospite aggiunto');
      setShowAggiungiOspite(false);
      setOspiteForm({ nome: '', squadra: '', note: '' });
      loadIscrizioni();
    } else {
      const err = await r?.json();
      showMsgLocal('error', err?.detail || 'Errore');
    }
    setSaving(false);
  };

  const rimuoviIscrizione = async (iscrizioneId, nome) => {
    showConfirm({
      title: 'Rimuovi Iscrizione',
      message: `Sei sicuro di voler rimuovere ${nome} dall'evento?`,
      confirmText: '🗑️ Rimuovi',
      cancelText: 'Annulla',
      variant: 'warning',
      onConfirm: async () => {
        const r = await api(`/api/clubs/calendario/eventi/${evento.id}/iscrizioni/${iscrizioneId}`, {
          method: 'DELETE'
        });
        
        if (r?.ok) {
          showMsgLocal('success', 'Iscrizione rimossa');
          loadIscrizioni();
        }
      }
    });
  };

  const getStatoBadge = () => {
    switch (statoIscrizioni) {
      case 'aperte':
        return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">✅ Aperte</span>;
      case 'chiuse':
        return <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">🔒 Chiuse</span>;
      case 'non_aperte':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">⏳ Non ancora aperte</span>;
      case 'complete':
        return <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">🏆 Complete</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">⚪ Disabilitate</span>;
    }
  };

  const copyEventLink = () => {
    const link = `${window.location.origin}/evento/${evento.id}`;
    navigator.clipboard.writeText(link);
    showMsgLocal('success', 'Link copiato!');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div 
          className="p-6 text-white flex justify-between items-start"
          style={{ backgroundColor: evento.colore || '#3b82f6' }}
        >
          <div>
            <p className="text-sm opacity-80">{evento.tipo_evento || 'Evento'}</p>
            <h3 className="text-xl font-bold">{evento.titolo}</h3>
            <p className="text-sm opacity-80 mt-1">📅 {formatDate(evento.data)} {evento.ora_inizio && `• 🕐 ${evento.ora_inizio}`}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">✕</button>
        </div>

        {/* Messages */}
        {msg.text && (
          <div className={`mx-6 mt-4 px-4 py-3 rounded-lg ${msg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {msg.text}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status & Link */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b">
            <div className="flex items-center gap-3">
              <span className="text-gray-600 font-medium">Stato:</span>
              {getStatoBadge()}
              {postiDisponibili !== null && (
                <span className="text-sm text-gray-500">
                  ({postiDisponibili} posti disponibili)
                </span>
              )}
            </div>
            <button
              onClick={copyEventLink}
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
            >
              🔗 Copia Link Evento
            </button>
          </div>

          {/* Settings Section */}
          <div className="bg-gray-50 rounded-xl p-5 space-y-4">
            <h4 className="font-bold text-gray-700 flex items-center gap-2">
              ⚙️ Impostazioni Iscrizioni
            </h4>
            
            {/* Abilita Iscrizioni */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.iscrizioni_abilitate}
                onChange={(e) => setSettings({ ...settings, iscrizioni_abilitate: e.target.checked })}
                className="w-5 h-5 rounded"
              />
              <span className="font-medium">Abilita Iscrizioni</span>
            </label>

            {settings.iscrizioni_abilitate && (
              <div className="grid md:grid-cols-2 gap-4 pt-2">
                {/* Apertura */}
                <div>
                  <label className="block text-sm font-medium mb-1">📅 Apertura Iscrizioni</label>
                  <input
                    type="datetime-local"
                    value={settings.iscrizioni_apertura || ''}
                    onChange={(e) => setSettings({ ...settings, iscrizioni_apertura: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Lascia vuoto per aprire subito</p>
                </div>

                {/* Chiusura */}
                <div>
                  <label className="block text-sm font-medium mb-1">🔒 Chiusura Iscrizioni</label>
                  <input
                    type="datetime-local"
                    value={settings.iscrizioni_chiusura || ''}
                    onChange={(e) => setSettings({ ...settings, iscrizioni_chiusura: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Lascia vuoto per non chiudere mai</p>
                </div>

                {/* Max Partecipanti */}
                <div>
                  <label className="block text-sm font-medium mb-1">👥 Max Partecipanti</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.max_partecipanti}
                    onChange={(e) => setSettings({ ...settings, max_partecipanti: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Illimitato"
                  />
                </div>

                {/* Copertina - Upload o URL */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">🖼️ Locandina / Copertina Evento</label>
                  
                  {/* Anteprima immagine corrente */}
                  {settings.copertina_url && (
                    <div className="mb-3 relative inline-block">
                      <img 
                        src={settings.copertina_url} 
                        alt="Anteprima copertina" 
                        className="max-h-48 rounded-lg border shadow-sm"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, copertina_url: '' })}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 shadow"
                        title="Rimuovi immagine"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    {/* Pulsante Upload */}
                    <label className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition ${uploadingCopertina ? 'bg-gray-400 cursor-wait' : 'bg-green-600 hover:bg-green-700'} text-white text-sm font-medium`}>
                      {uploadingCopertina ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          Caricamento...
                        </>
                      ) : (
                        <>
                          <span>📤</span>
                          Carica dal PC
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleCopertinaUpload}
                        disabled={uploadingCopertina}
                        className="hidden"
                      />
                    </label>
                    
                    {/* Oppure inserisci URL */}
                    <div className="flex-1 flex items-center gap-2">
                      <span className="text-gray-400 text-sm hidden sm:inline">oppure</span>
                      <input
                        type="url"
                        value={settings.copertina_url}
                        onChange={(e) => setSettings({ ...settings, copertina_url: e.target.value })}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        placeholder="Incolla URL immagine..."
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Formati supportati: JPG, PNG, GIF, WebP (max 5MB)</p>
                </div>

                {/* Regolamento */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">📋 Regolamento / Descrizione</label>
                  <textarea
                    value={settings.regolamento}
                    onChange={(e) => setSettings({ ...settings, regolamento: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm h-24"
                    placeholder="Descrivi la formula del torneo, regole, premi..."
                  />
                </div>

                {/* Tema Torneo */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">🎨 Tema del Torneo</label>
                  <input
                    type="text"
                    value={settings.tema_torneo}
                    onChange={(e) => setSettings({ ...settings, tema_torneo: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Es. Mondiale 1990, Serie A anni 80, Champions League..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Il tema apparirà nella pagina evento e nel popup iscrizione</p>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center pt-2">
              {/* Pulsante chiudi/riapri iscrizioni */}
              {settings.iscrizioni_abilitate && (
                <button
                  onClick={toggleChiusuraIscrizioni}
                  disabled={saving}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    settings.iscrizioni_chiuse_manualmente
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                >
                  {settings.iscrizioni_chiuse_manualmente ? '🔓 Riapri Iscrizioni' : '🔒 Chiudi Iscrizioni'}
                </button>
              )}
              <div className="flex-1" />
              <button
                onClick={saveSettings}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {saving ? 'Salvataggio...' : '💾 Salva Impostazioni'}
              </button>
            </div>
          </div>

          {/* Lista Iscritti */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-bold text-gray-700">
                👥 Iscritti ({iscrizioni.length}{settings.max_partecipanti ? `/${settings.max_partecipanti}` : ''})
              </h4>
              <button
                onClick={() => setShowAggiungiOspite(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                + Aggiungi Ospite
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-500">Caricamento...</div>
            ) : iscrizioni.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                <div className="text-3xl mb-2">👥</div>
                <p>Nessun iscritto</p>
              </div>
            ) : (
              <div className="bg-white border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partecipante</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Squadra</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {iscrizioni.map((isc, idx) => (
                      <tr key={isc.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-3 font-medium">{isc.partecipante_nome}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                            ⚽ {isc.squadra}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {isc.tipo_partecipante === 'ospite' ? (
                            <span className="text-orange-600">🎫 Ospite</span>
                          ) : (
                            <span className="text-green-600">👤 Socio</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDateTime(isc.registered_at)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => rimuoviIscrizione(isc.id, isc.partecipante_nome)}
                            className="text-red-600 hover:text-red-800 px-2"
                            title="Rimuovi"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Lista d'Attesa */}
            {listaAttesa.length > 0 && (
              <div className="mt-6">
                <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="text-orange-500">🕐</span> Lista d'Attesa ({listaAttesa.length})
                </h4>
                <div className="bg-orange-50 border border-orange-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-orange-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-orange-700 uppercase">Pos.</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-orange-700 uppercase">Partecipante</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-orange-700 uppercase">Squadra</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-orange-700 uppercase">Data</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-orange-700 uppercase">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-orange-200">
                      {listaAttesa.map((isc, idx) => (
                        <tr key={isc.id} className="hover:bg-orange-100/50">
                          <td className="px-4 py-2 text-sm font-bold text-orange-600">{idx + 1}°</td>
                          <td className="px-4 py-2 font-medium">{isc.partecipante_nome}</td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-orange-200 text-orange-800 rounded text-sm">
                              ⚽ {isc.squadra}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {formatDateTime(isc.created_at)}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <button
                              onClick={() => rimuoviIscrizione(isc.id, isc.partecipante_nome)}
                              className="text-red-600 hover:text-red-800 px-2"
                              title="Rimuovi dalla lista"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-orange-600 mt-2">
                  💡 Quando un iscritto annulla, il primo in lista d'attesa viene promosso automaticamente
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
          >
            Chiudi
          </button>
        </div>

        {/* Modal Aggiungi Ospite */}
        {showAggiungiOspite && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b">
                <h3 className="text-lg font-bold">🎫 Aggiungi Ospite</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome *</label>
                  <input
                    type="text"
                    value={ospiteForm.nome}
                    onChange={(e) => setOspiteForm({ ...ospiteForm, nome: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Nome e cognome ospite"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Squadra *</label>
                  <input
                    type="text"
                    value={ospiteForm.squadra}
                    onChange={(e) => setOspiteForm({ ...ospiteForm, squadra: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Es: Inter, Milan, Juventus..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Note</label>
                  <input
                    type="text"
                    value={ospiteForm.note}
                    onChange={(e) => setOspiteForm({ ...ospiteForm, note: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Note opzionali..."
                  />
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => setShowAggiungiOspite(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annulla
                </button>
                <button
                  onClick={aggiungiOspite}
                  disabled={saving}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  {saving ? 'Aggiunta...' : 'Aggiungi'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ PAGINA EVENTO PUBBLICA (per Soci) ============

export function PaginaEventoPubblica({ eventoId }) {
  const [evento, setEvento] = useState(null);
  const [club, setClub] = useState(null);
  const [iscrizioni, setIscrizioni] = useState([]);
  const [listaAttesa, setListaAttesa] = useState([]);
  const [statoIscrizioni, setStatoIscrizioni] = useState('');
  const [postiDisponibili, setPostiDisponibili] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Form iscrizione
  const [showIscrizione, setShowIscrizione] = useState(false);
  const [iscrizioneForm, setIscrizioneForm] = useState({
    partecipante_nome: '',
    squadra: ''
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [waitlistPosition, setWaitlistPosition] = useState(null);

  const loadEvento = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/public/eventi/${eventoId}`);
      if (!r.ok) {
        setError('Evento non trovato o non disponibile');
        setLoading(false);
        return;
      }
      const data = await r.json();
      setEvento(data.evento);
      setClub(data.club);
      setIscrizioni(data.iscrizioni || []);
      setListaAttesa(data.lista_attesa || []);
      setStatoIscrizioni(data.stato_iscrizioni);
      setPostiDisponibili(data.posti_disponibili);
    } catch (err) {
      setError('Errore caricamento evento');
    }
    setLoading(false);
  }, [eventoId]);

  useEffect(() => {
    loadEvento();
  }, [loadEvento]);

  const showMsgLocal = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 5000);
  };

  const handleIscrizione = async () => {
    if (!iscrizioneForm.partecipante_nome.trim()) {
      showMsgLocal('error', 'Inserisci il tuo nome');
      return;
    }
    if (!iscrizioneForm.squadra.trim()) {
      showMsgLocal('error', 'Inserisci la squadra che utilizzerai');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/eventi/${eventoId}/iscrizione`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(iscrizioneForm)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.in_lista_attesa) {
          setWaitlistPosition(data.posizione);
          showMsgLocal('success', data.message || `Sei in lista d'attesa (posizione ${data.posizione})`);
        } else {
          showMsgLocal('success', data.message || 'Iscrizione completata!');
        }
        setIscrizioneForm({ partecipante_nome: '', squadra: '' });
        setShowIscrizione(false);
        loadEvento(); // Ricarica lista iscritti
      } else {
        showMsgLocal('error', data.detail || 'Errore durante l\'iscrizione');
      }
    } catch (err) {
      showMsgLocal('error', 'Errore di connessione');
    }
    setSaving(false);
  };

  const getCountdown = () => {
    if (!evento?.iscrizioni_apertura) return null;
    
    const apertura = new Date(evento.iscrizioni_apertura);
    const now = new Date();
    
    if (now >= apertura) return null;
    
    const diff = apertura - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { days, hours, minutes };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md">
          <div className="text-5xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Oops!</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const countdown = getCountdown();

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Copertina */}
      {evento.copertina_url ? (
        <div 
          className="h-64 bg-cover bg-center relative"
          style={{ backgroundImage: `url(${evento.copertina_url})` }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
      ) : (
        <div 
          className="h-48"
          style={{ backgroundColor: evento.colore || '#3b82f6' }}
        ></div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 -mt-20 relative z-10 pb-12">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="p-8 border-b">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                {evento.tipo_evento && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm mb-2 inline-block">
                    {evento.tipo_evento}
                  </span>
                )}
                <h1 className="text-3xl font-bold text-gray-800">{evento.titolo}</h1>
                {evento.tema_torneo && (
                  <p className="text-lg text-purple-600 font-medium mt-1">
                    🎨 Tema: {evento.tema_torneo}
                  </p>
                )}
                {club && (
                  <p className="text-gray-500 mt-1">
                    🏆 {club.club_name} {club.city && `• 📍 ${club.city}`}
                  </p>
                )}
              </div>
              
              {/* Stato Badge + Posti */}
              <div className="text-right space-y-2">
                {statoIscrizioni === 'aperte' && (
                  <span className="px-4 py-2 bg-green-500 text-white rounded-full font-medium block">
                    ✅ Iscrizioni Aperte
                  </span>
                )}
                {statoIscrizioni === 'chiuse' && (
                  <span className="px-4 py-2 bg-red-500 text-white rounded-full font-medium block">
                    🔒 Iscrizioni Chiuse
                  </span>
                )}
                {statoIscrizioni === 'chiuse_manualmente' && (
                  <span className="px-4 py-2 bg-red-500 text-white rounded-full font-medium block">
                    🔒 Iscrizioni Chiuse
                  </span>
                )}
                {statoIscrizioni === 'non_aperte' && (
                  <span className="px-4 py-2 bg-yellow-500 text-white rounded-full font-medium block">
                    ⏳ Prossimamente
                  </span>
                )}
                {statoIscrizioni === 'complete' && (
                  <span className="px-4 py-2 bg-purple-500 text-white rounded-full font-medium block">
                    🏆 Posti Esauriti
                  </span>
                )}
                
                {/* Badge Posti Colorato */}
                {evento.max_partecipanti && (
                  <div className={`px-4 py-3 rounded-xl text-center font-bold text-lg ${
                    postiDisponibili === 0 
                      ? 'bg-red-100 text-red-700' 
                      : postiDisponibili <= 2 
                        ? 'bg-orange-100 text-orange-700' 
                        : 'bg-green-100 text-green-700'
                  }`}>
                    {postiDisponibili === 0 
                      ? `🔴 ${iscrizioni.length}/${evento.max_partecipanti} Esauriti`
                      : postiDisponibili <= 2 
                        ? `⚠️ ${iscrizioni.length}/${evento.max_partecipanti} posti`
                        : `✅ ${iscrizioni.length}/${evento.max_partecipanti} posti`
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Info Evento */}
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              <div className="flex items-center gap-3 text-gray-700">
                <span className="text-2xl">📅</span>
                <div>
                  <p className="text-sm text-gray-500">Data</p>
                  <p className="font-medium">{formatDate(evento.data)}</p>
                </div>
              </div>
              {evento.ora_inizio && (
                <div className="flex items-center gap-3 text-gray-700">
                  <span className="text-2xl">🕐</span>
                  <div>
                    <p className="text-sm text-gray-500">Orario</p>
                    <p className="font-medium">{evento.ora_inizio}{evento.ora_fine && ` - ${evento.ora_fine}`}</p>
                  </div>
                </div>
              )}
              {evento.luogo && (
                <div className="flex items-center gap-3 text-gray-700">
                  <span className="text-2xl">📍</span>
                  <div>
                    <p className="text-sm text-gray-500">Luogo</p>
                    <p className="font-medium">{evento.luogo}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Countdown (se non aperte) */}
          {countdown && (
            <div className="bg-yellow-50 p-6 border-b">
              <p className="text-center text-yellow-800 font-medium mb-3">
                ⏳ Le iscrizioni apriranno tra:
              </p>
              <div className="flex justify-center gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-700">{countdown.days}</div>
                  <div className="text-xs text-yellow-600">giorni</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-700">{countdown.hours}</div>
                  <div className="text-xs text-yellow-600">ore</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-700">{countdown.minutes}</div>
                  <div className="text-xs text-yellow-600">minuti</div>
                </div>
              </div>
              <p className="text-center text-sm text-yellow-600 mt-3">
                Apertura: {formatDateTime(evento.iscrizioni_apertura)}
              </p>
            </div>
          )}

          {/* Messaggio */}
          {msg.text && (
            <div className={`mx-6 mt-4 px-4 py-3 rounded-lg ${msg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {msg.text}
            </div>
          )}

          {/* Descrizione / Regolamento */}
          {(evento.descrizione || evento.regolamento) && (
            <div className="p-6 border-b">
              {evento.descrizione && (
                <div className="mb-4">
                  <h3 className="font-bold text-gray-700 mb-2">📝 Descrizione</h3>
                  <p className="text-gray-600 whitespace-pre-wrap">{evento.descrizione}</p>
                </div>
              )}
              {evento.regolamento && (
                <div>
                  <h3 className="font-bold text-gray-700 mb-2">📋 Regolamento</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-600 whitespace-pre-wrap">{evento.regolamento}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Lista Iscritti */}
          <div className="p-6">
            <h3 className="font-bold text-gray-700 mb-4 text-lg">
              👥 Iscritti ({iscrizioni.length}{evento.max_partecipanti ? `/${evento.max_partecipanti}` : ''})
            </h3>
            
            {iscrizioni.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                <div className="text-3xl mb-2">👥</div>
                <p>Nessun iscritto ancora</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {iscrizioni.map((isc, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-orange-300 text-orange-800' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-800 truncate">👤 {isc.partecipante_nome}</p>
                        <p className="text-blue-600 font-medium">⚽ {isc.squadra}</p>
                        {isc.created_at && (
                          <p className="text-xs text-gray-400 mt-1">
                            📅 {formatDateTime(isc.created_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Lista d'Attesa (se presente) */}
            {listaAttesa.length > 0 && (
              <div className="mt-6">
                <h3 className="font-bold text-orange-600 mb-4 text-lg flex items-center gap-2">
                  <span>🕐</span> Lista d'Attesa ({listaAttesa.length})
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {listaAttesa.map((isc, idx) => (
                    <div key={idx} className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <span className="w-10 h-10 bg-orange-200 text-orange-700 rounded-full flex items-center justify-center font-bold">
                          {idx + 1}°
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-800 truncate">👤 {isc.partecipante_nome}</p>
                          <p className="text-orange-600 font-medium">⚽ {isc.squadra}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CTA Iscrizione / Lista d'Attesa */}
          {(statoIscrizioni === 'aperte' || statoIscrizioni === 'complete') && (
            <div className={`p-6 border-t ${statoIscrizioni === 'complete' ? 'bg-orange-50' : 'bg-green-50'}`}>
              {!showIscrizione ? (
                <div className="text-center">
                  {statoIscrizioni === 'aperte' ? (
                    <>
                      <p className="text-green-700 mb-4">
                        Vuoi partecipare a questo evento? Iscriviti ora!
                      </p>
                      <button 
                        onClick={() => setShowIscrizione(true)}
                        className="px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                        data-testid="btn-apri-iscrizione"
                      >
                        ✋ Iscriviti Ora
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-orange-700 mb-4">
                        I posti sono esauriti, ma puoi metterti in lista d'attesa!
                      </p>
                      <button 
                        onClick={() => setShowIscrizione(true)}
                        className="px-8 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
                        data-testid="btn-lista-attesa"
                      >
                        🕐 Mettimi in Lista d'Attesa
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="max-w-md mx-auto bg-white rounded-xl p-6 shadow-lg">
                  <h3 className={`text-lg font-bold mb-2 text-center ${statoIscrizioni === 'complete' ? 'text-orange-800' : 'text-green-800'}`}>
                    {statoIscrizioni === 'complete' ? '🕐 Iscrizione Lista d\'Attesa' : '✍️ Conferma la tua Iscrizione'}
                  </h3>
                  
                  {/* Mostra evento e tema */}
                  <div className="text-center mb-4 pb-4 border-b">
                    <p className="font-medium text-gray-700">{evento.titolo}</p>
                    {evento.tema_torneo && (
                      <p className="text-purple-600 text-sm mt-1">
                        🎨 Il tema del torneo è: <strong>{evento.tema_torneo}</strong>
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Il tuo Nome *
                      </label>
                      <input
                        type="text"
                        value={iscrizioneForm.partecipante_nome}
                        onChange={(e) => setIscrizioneForm({ ...iscrizioneForm, partecipante_nome: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Es: Mario Rossi"
                        data-testid="input-nome-iscrizione"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Squadra che utilizzerai *
                      </label>
                      <input
                        type="text"
                        value={iscrizioneForm.squadra}
                        onChange={(e) => setIscrizioneForm({ ...iscrizioneForm, squadra: e.target.value })}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${statoIscrizioni === 'complete' ? 'focus:ring-orange-500 focus:border-orange-500' : 'focus:ring-green-500 focus:border-green-500'}`}
                        placeholder={evento.tema_torneo ? `Es. ${evento.tema_torneo === 'Mondiale 1990' ? 'Italia 1990, Germania 1990...' : 'Italia 1982, Ajax 1995...'}` : "Es: Italia 1982, Ajax 1995..."}
                        data-testid="input-squadra-iscrizione"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {evento.tema_torneo 
                          ? `Scegli una squadra coerente con il tema "${evento.tema_torneo}"`
                          : 'Indica la squadra con cui parteciperai al torneo'
                        }
                      </p>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => setShowIscrizione(false)}
                        className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        Annulla
                      </button>
                      <button
                        onClick={handleIscrizione}
                        disabled={saving}
                        className={`flex-1 px-4 py-3 text-white rounded-lg font-medium disabled:bg-gray-400 ${
                          statoIscrizioni === 'complete' 
                            ? 'bg-orange-500 hover:bg-orange-600' 
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                        data-testid="btn-conferma-iscrizione"
                      >
                        {saving ? 'Iscrizione...' : statoIscrizioni === 'complete' ? '🕐 Entra in Lista' : '✅ Conferma Iscrizione'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GestioneIscrizioniEvento;
